import { yupResolver } from "@hookform/resolvers/yup";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  ITabs,
  MuiModal,
  MuiTabPanel,
  MuiTabs,
  WarningConfirmationModal,
} from "../../../../components";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  ActivityDeliverableType,
  CreateActivityDefaultFormKey,
  CreateActivityDefaultProps,
  CreateActivityGeneralFormKey,
  CreateActivityGeneralProps,
  EditActivityValueProps,
  ProgramTemplateActivitiesRowValue,
} from "../../modelsAndSchemas/Models";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { CreateActivityWrapper } from "./CreateActivity.style";
import { CreateActivityGeneral } from "./createActivityTabs/createActivityGeneral/CreateActivityGeneral";

import { useMdmSearchActivityMutation } from "../../../../api/services/mdmActivity/mdmActivityApis";
import {
  useCreateActivityMutation,
  useGetListOfDeliverables,
  useUpdateProgramTemplateActivityMutation,
} from "../../../../api/services/programTemplate/programTemplateApis";
import { InformationBadge } from "../../../../common/informationBadge/informationBadge";
import { UnitTypesKey } from "../../../../helpers/constants/siteCoreKeys";
import {
  DefaultValueExceptionType,
  KeyValue,
} from "../../../../helpers/types/common";
import {
  convertFormDataToLocalizeObj,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../helpers/utils";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import {
  MdmSearchActivityResultRowValue,
  ModalityType,
} from "../../../mdmActivitySearch/modelsAndSchemas/models";
import {
  CreateActivityDefaultValidationSchema,
  CreateActivityGeneralValidationSchema,
} from "../../modelsAndSchemas/ValidationSchema";
import { CancelFieldModal } from "../cancelFieldModal/CancelFieldModal";
import { CreateActivityDefault } from "./createActivityTabs/createActivityDefault/CreateActivityDefault";
import { userPreferredLanguageSelector } from "../../../../redux/slices/appState/appStateSelector";
import { LANGUAGE_CODE } from "../../../../helpers/constants";

interface CreateActivityProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  programTemplateId: string;
  getListOfActivitiesApi: () => void;
  isEditActivity?: boolean;
  editActivityData?: EditActivityValueProps;
  isPendingActivity?: boolean;
  selectedActivity?: ProgramTemplateActivitiesRowValue;
  frameWorkId?: string;
}

export const CreateActivity: FC<CreateActivityProps> = ({
  show,
  handleCloseModal,
  pageId,
  programTemplateId,
  getListOfActivitiesApi,
  isEditActivity = false,
  editActivityData,
  isPendingActivity = false,
  selectedActivity,
  frameWorkId = "",
}) => {
  const dispatch = useDispatch();
  const userSelectedLanguage: string = useSelector(
    userPreferredLanguageSelector,
  );
  const deliveryRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const participantStatusRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);
  const modalityRef = useRef<{ resetValues: (values?: string[]) => void }>(
    null,
  );
  const [selectedCountry, setSelectedCountry] = useState("");
  const [showManageLocale, setShowManageLocale] = useState(false);

  const [selectedTab, setSelectedTab] = useState(0);
  const [showCancel, setShowCancel] = useState(false);
  const [exceptionsShouldReset, setExceptionsShouldReset] = useState(false);
  const {
    commonLabels,
    fetchLabel,
    fetchComponentTitle,
    commonValidationMessages,
    fetchDropdownValues,
  } = useSiteCore();

  const [exceptions, setExceptions] = useState<DefaultValueExceptionType[]>([]);
  const indexRef = useRef<string>();
  const [showExceptioṇModal, setShowExceptioṇModal] = useState(false);
  const [topicNameValue, setTopicNameValue] = useState("");

  const {
    mutateAsync: saveActivityApi,
    isPending: isSavePending,
    isError: isSaveError,
    isSuccess: isSaveSuccess,
  } = useCreateActivityMutation();

  const {
    mutateAsync: updateActivityApi,
    isSuccess: isUpdateSuccess,
    isPending: isUpdatePending,
    isError: isUpdateError,
  } = useUpdateProgramTemplateActivityMutation();

  const {
    data: activityDeliverables,
    isFetching: activityDeliverablesPending,
    isError: isActivityDeliverablesError,
    isSuccess: isActivityDeliverablesSuccess,
    refetchWithParams: getListOfDeliverablesApi,
  } = useGetListOfDeliverables();

  const {
    mutateAsync: getMdmSearchActivityList,
    isPending: activityTypesPending,
    data: activityTypeResult,
    isError: isActivityTypeError,
  } = useMdmSearchActivityMutation();

  useEffect(() => {
    if (programTemplateId) {
      getListOfDeliverablesApi(programTemplateId, frameWorkId);
      const params = {
        activityTypeFilter: "",
        activityType: "",
        topicFilter: "",
        topic: "",
        activityStatus: "",
        programFrameworkId: frameWorkId,
        ...(isEditActivity && {
          id: selectedActivity?.id,
        }),
      };
      // all MDM activities will be fetched with default params
      getMdmSearchActivityList(params);
    }
  }, [programTemplateId]);

  const getDeliverablesData = () => {
    if (activityDeliverables && activityDeliverables.deliverables) {
      activityDeliverables.deliverables.forEach((item: any) => {});
      return (
        (activityDeliverables.deliverables as ActivityDeliverableType[]) || []
      );
    } else return [];
  };

  useEffect(() => {
    if (isSavePending || isUpdatePending) {
      dispatch(
        toggleLoader({
          isVisible: isSavePending || isUpdatePending,
        }),
      );
    }

    if (
      activityDeliverablesPending ||
      isPendingActivity ||
      activityTypesPending
    ) {
      dispatch(
        toggleLoader({
          isVisible:
            activityDeliverablesPending ||
            isPendingActivity ||
            activityTypesPending,
          message: commonLabels.loadingPleaseWait,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isSavePending,
    isUpdatePending,
    activityDeliverablesPending,
    isPendingActivity,
    activityTypesPending,
  ]);

  useEffect(() => {
    if (isUpdateSuccess || isSaveSuccess) {
      dispatch(
        toggleToast({
          open: true,
          message: fetchLabel(
            createProgramSiteCoreKeys.createActivityKey,
            isUpdateSuccess
              ? createProgramSiteCoreKeys.activityUpdatedMessage
              : createProgramSiteCoreKeys.activityCreatedMessage,
          ),
        }),
      );
      getListOfActivitiesApi();
      handleCloseModal();
    }
    if (
      isSaveError ||
      isUpdateError ||
      isActivityDeliverablesError ||
      isActivityTypeError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isSaveSuccess,
    isSaveError,
    isUpdateError,
    isUpdateSuccess,
    isActivityDeliverablesError,
    isActivityTypeError,
  ]);

  const tabIdGeneral = `${pageId}-tab1`;
  const tabIdActivity = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdGeneral,
      label: fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        createProgramSiteCoreKeys.generalTab,
      ),
    },
    {
      id: tabIdActivity,
      label: fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        createProgramSiteCoreKeys.defaultActivityValues,
      ),
    },
  ];

  const defaultValuesGeneral = {
    [CreateActivityGeneralFormKey.candidateStatus]: [],
    [CreateActivityGeneralFormKey.activityType]: "",
    [CreateActivityGeneralFormKey.modality]: [],
    [CreateActivityGeneralFormKey.topic]: {
      [userSelectedLanguage]: "",
    },
    [CreateActivityGeneralFormKey.deliverables]: [],
    [CreateActivityGeneralFormKey.activityStatus]: "",
    [CreateActivityGeneralFormKey.triggerBilling]: false,
    [CreateActivityGeneralFormKey.lastContactDate]: false,
    [CreateActivityGeneralFormKey.triggerTouch]: false,
  };

  const defaultValueDefault = {
    [CreateActivityDefaultFormKey.units]: "",
    [CreateActivityDefaultFormKey.measurementMinute]: "",
    [CreateActivityDefaultFormKey.measurementOther]: undefined,
    [CreateActivityDefaultFormKey.country]: "",
    [CreateActivityDefaultFormKey.countryMeasurementMinute]: "",
    [CreateActivityDefaultFormKey.countryMeasurementOther]: undefined,
  };

  //form for general tab
  const {
    handleSubmit: handleSubmitGeneral,
    control: controlGeneralTab,
    getValues: generalTabValues,
    formState: { isDirty: isDirtyGeneralForm, dirtyFields },
    reset: resetGeneralForm,
    setValue: setGeneralTabValue,
  } = useForm<CreateActivityGeneralProps>({
    mode: "all",
    defaultValues: defaultValuesGeneral,
    resolver: yupResolver(
      CreateActivityGeneralValidationSchema(
        commonValidationMessages,
        userSelectedLanguage,
      ),
    ),
  });

  //form form default tab
  const {
    handleSubmit: handleSubmitDefault,
    control: controlDefaultTab,
    getValues: defaultTabValues,
    formState: { isDirty: isDirtyDefaultForm },
    watch,
    resetField,
    reset: resetDefaultForm,
    setValue,
  } = useForm<CreateActivityDefaultProps>({
    mode: "all",
    defaultValues: defaultValueDefault,
    resolver: yupResolver(
      CreateActivityDefaultValidationSchema(commonValidationMessages),
    ),
  });

  const activityTypeWatch = useWatch({
    control: controlGeneralTab,
    name: CreateActivityGeneralFormKey.activityType,
  });

  // will be based on selected activity type
  const selectedModalityList = useMemo(() => {
    const selectedActivityTypeId = generalTabValues().activityType;

    if (dirtyFields?.activityType) {
      setGeneralTabValue(CreateActivityGeneralFormKey.modality, []);
      modalityRef.current?.resetValues([]);
    }

    const modalityListDropdownValues = fetchDropdownValues(
      createProgramSiteCoreKeys.createActivityKey,
      createProgramSiteCoreKeys.modalityList,
    );

    const modalityMap = new Map(
      modalityListDropdownValues.map(({ value, label }) => [value, label]),
    );

    if (!selectedActivityTypeId || !Array.isArray(activityTypeResult?.data)) {
      return [];
    }

    const matchedActivity = activityTypeResult?.data?.find(
      (item) => item.id === selectedActivityTypeId,
    );

    return (
      matchedActivity?.modalityLst.map((v: ModalityType) => ({
        value: v.modalityName,
        label: modalityMap.get(v.modalityName) ?? v.modalityName,
      })) ?? []
    );
  }, [activityTypeWatch]);

  useEffect(() => {
    if (
      isNotEmpty(editActivityData) &&
      isEditActivity &&
      isNotEmpty(activityTypeResult?.data)
    ) {
      resetGeneralForm({
        ...editActivityData,
        activityType: editActivityData?.activityType?.toLowerCase() ?? "",
      });
      resetDefaultForm(editActivityData);
      deliveryRef.current?.resetValues(editActivityData?.deliverables ?? []);
      setTimeout(() => {
        modalityRef.current?.resetValues(editActivityData?.modality ?? []);
      }, 0);
      participantStatusRef.current?.resetValues(
        editActivityData?.candidateStatus ?? [],
      );
      if (isNotEmpty(editActivityData?.defaultValueExceptions)) {
        const updatedExceptions = editActivityData?.defaultValueExceptions.map(
          (element: any) => ({
            ...element,
            country: element.countryTitle,
          }),
        );
        setExceptions(updatedExceptions ?? []);
      } else {
        setExceptions([]); // Reset if no exceptions are found
      }
      setExceptionsShouldReset(false);

      setTopicNameValue(
        getValueOrBlank(editActivityData?.topic[userSelectedLanguage]),
      );
    }
  }, [
    editActivityData,
    isEditActivity,
    isPendingActivity,
    activityDeliverablesPending,
    activityTypesPending,
  ]);

  useEffect(() => {
    if (isEditActivity && !editActivityData) {
      return;
    }
    const units = defaultTabValues().units;
    if (
      units &&
      isEditActivity &&
      !exceptionsShouldReset &&
      units === editActivityData?.units
    ) {
      setExceptionsShouldReset(true);
      return;
    }

    setExceptions([]);
    resetDefaultForm({
      ...defaultValueDefault,
      [CreateActivityDefaultFormKey.units]: defaultTabValues().units,
      [CreateActivityDefaultFormKey.measurementOther]: null,
    });
    setSelectedCountry("");
  }, [watch(CreateActivityDefaultFormKey.units)]);

  useEffect(() => {
    resetField(CreateActivityDefaultFormKey.countryMeasurementOther);
    resetField(CreateActivityDefaultFormKey.countryMeasurementMinute);
  }, [watch(CreateActivityDefaultFormKey.country)]);

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    if (value === 1) {
      onConfirmClick(); // check if user entered mandatory fields or not
    } else {
      setSelectedTab(value);
    }
  };

  //first tab click
  const onNextClick = () => {
    setSelectedTab(1);
  };

  useEffect(() => {
    const matched = activityTypeResult?.data?.find(
      (item: { id: string }) => item.id === activityTypeWatch,
    );

    if (matched?.units) {
      setValue(CreateActivityDefaultFormKey.units, matched?.units, {
        shouldValidate: true,
      });
    }
  }, [activityTypeWatch, activityTypeResult?.data]);

  //second tab click
  const onFinalSubmitClick = () => {
    const {
      activityType,
      modality,
      topic,
      activityStatus,
      candidateStatus,
      deliverables,
      triggerBilling,
      lastContactDate,
      triggerTouch,
    } = generalTabValues();
    const { units, measurementMinute, measurementOther } = defaultTabValues();

    const otherMeasurement =
      units === UnitTypesKey.Count || units === UnitTypesKey.Days
        ? (measurementOther as number).toString()
        : "1";

    const measurement =
      units === UnitTypesKey.Minutes ? measurementMinute : otherMeasurement;

    const defaultValueExceptions: DefaultValueExceptionType[] = [];

    exceptions.forEach((item) => {
      defaultValueExceptions.push({
        countryCode: item.countryCode,
        units: item.units,
        measurement: item.measurement,
      });
    });

    if (isEmpty(topic[LANGUAGE_CODE.EN_US])) {
      topic[LANGUAGE_CODE.EN_US] = topic[userSelectedLanguage];
    }

    const apiData = {
      programTemplateId,
      activityType,
      modality,
      topic: topic[LANGUAGE_CODE.EN_US],
      localizedTopics: convertFormDataToLocalizeObj(topic),
      activityStatus,
      candidateStatus,
      deliverableIds: deliverables,
      triggerBilling,
      countsTowardsLastContactDate: lastContactDate,
      triggerTouchPointSurvey: triggerTouch,
      units,
      measurement: measurement,
      defaultValueExceptions,
    };
    if (isEditActivity) {
      updateActivityApi({
        ...apiData,
        activityId: selectedActivity?.id ?? "",
        activityName:
          activityTypeResult?.data?.find(
            (item: MdmSearchActivityResultRowValue) => item.id === activityType,
          )?.activityType ?? "",
        programTemplateId: programTemplateId,
      });
    } else {
      saveActivityApi(apiData);
    }
  };

  const onConfirmClick = () => {
    if (selectedTab === 0) {
      handleSubmitGeneral(onNextClick)();
    } else {
      handleSubmitDefault(onFinalSubmitClick)();
    }
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelMainModalHandler = () => {
    if (isDirtyDefaultForm || isDirtyGeneralForm) setShowCancel(true);
    else handleCloseModal();
  };

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    handleCloseModal();
  };

  const addExceptionHandler = () => {
    const {
      units,
      country,
      countryMeasurementMinute,
      countryMeasurementOther,
    } = defaultTabValues();

    handleSubmitDefault(() => {
      if (units && country) {
        const otherMeasurement =
          units === UnitTypesKey.Count || units === UnitTypesKey.Days
            ? countryMeasurementOther.toString()
            : "1";

        const measurement =
          units === UnitTypesKey.Minutes
            ? countryMeasurementMinute
            : otherMeasurement;

        setExceptions((prev) => [
          ...prev,
          {
            countryCode: selectedCountry,
            units: units,
            country: country,
            measurement: measurement,
            mesurementName: "",
          },
        ]);

        resetField(CreateActivityDefaultFormKey.country);
        setSelectedCountry("");
      }
    })();
  };

  const handleModalClose = () => {
    if (showExceptioṇModal) {
      setShowExceptioṇModal(false);
    }
  };
  const handleDeleteClick = () => {
    if (isNotEmpty(indexRef.current)) {
      let countryCode = indexRef.current;
      setShowExceptioṇModal(false);
      const idx = exceptions.findIndex(
        (item) => item.countryCode === countryCode,
      );
      if (idx > -1) {
        const newExceptions = [...exceptions];
        newExceptions.splice(idx, 1);
        setExceptions(newExceptions);
      }
    }
  };
  const deleteAction = (countryCode: string) => {
    if (showExceptioṇModal === false) {
      indexRef.current = countryCode;
      setShowExceptioṇModal(true);
    }
  };

  const activityHeaderText = () => {
    if (isEditActivity) {
      return fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        selectedActivity?.programFrameworkId
          ? createProgramSiteCoreKeys.isEditFrameworkText
          : createProgramSiteCoreKeys.activityNote,
      );
    } else {
      return fetchLabel(
        createProgramSiteCoreKeys.createActivityKey,
        createProgramSiteCoreKeys.willTemplateNote,
      );
    }
  };

  const activityTitle = () => {
    if (isEditActivity) {
      return replaceHashWithValue(
        fetchLabel(
          createProgramSiteCoreKeys.createActivityKey,
          createProgramSiteCoreKeys.editActivityPopupTitle,
        ),
        editActivityData?.activityName ?? "",
      );
    } else {
      return fetchComponentTitle(createProgramSiteCoreKeys.createActivityKey);
    }
  };

  const editActivityLabel: string = isEditActivity
    ? commonLabels.update
    : commonLabels.save;

  const getPrimaryButtonTitle = () => {
    return selectedTab === 0 ? commonLabels.next : editActivityLabel;
  };

  return (
    <MuiModal
      open={show}
      onClose={cancelMainModalHandler}
      title={activityTitle()}
      primaryButtonTitle={getPrimaryButtonTitle()}
      secondaryButtonTitle={commonLabels.cancel}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={cancelMainModalHandler}
      hasLargeContent
      className={showManageLocale ? "hide-modal" : ""}
    >
      <CreateActivityWrapper>
        <InformationBadge message={activityHeaderText()} />

        <MuiTabs
          tabs={tabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={tabs.map((tab) => tab.id)}
        >
          <MuiTabPanel
            panelId={`${pageId}-panel1`}
            value={selectedTab}
            index={0}
            ariaLabelledById={tabIdGeneral}
            isPanelWithMount
          >
            <CreateActivityGeneral
              control={controlGeneralTab}
              pageId={pageId}
              deliveryRef={deliveryRef}
              activityDeliverables={getDeliverablesData()}
              isFrameworkActivity={isNotEmpty(
                selectedActivity?.programFrameworkId,
              )}
              frameworkValue={selectedActivity?.programFramework ?? ""}
              participantStatusRef={participantStatusRef}
              modalityRef={modalityRef}
              activityTypeList={activityTypeResult?.data ?? []}
              modalityList={selectedModalityList}
              isActivityDeliverablesSuccess={isActivityDeliverablesSuccess}
              handleSaveManageLocal={(manageLocals: KeyValue) =>
                setGeneralTabValue(
                  CreateActivityGeneralFormKey.topic,
                  manageLocals,
                )
              }
              manageLocaleTitle={activityTitle()}
              showManageLocale={showManageLocale}
              setShowManageLocale={setShowManageLocale}
              bannerInfoMessage={activityHeaderText()}
              isEditActivity={isEditActivity}
              topicNameValue={topicNameValue}
              setTopicNameValue={setTopicNameValue}
            />
          </MuiTabPanel>
          <MuiTabPanel
            panelId={`${pageId}-panel2`}
            value={selectedTab}
            index={1}
            ariaLabelledById={tabIdActivity}
          >
            <CreateActivityDefault
              control={controlDefaultTab}
              pageId={pageId}
              rows={exceptions}
              addExceptionHandler={addExceptionHandler}
              deleteAction={deleteAction}
              setSelectedCountry={setSelectedCountry}
              selectedCountry={selectedCountry}
              unitValue={defaultTabValues().units}
            />
          </MuiTabPanel>
        </MuiTabs>
        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
          />
        )}

        {showExceptioṇModal && (
          <WarningConfirmationModal
            show={showExceptioṇModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteClick}
            labelContent={fetchLabel(
              createProgramSiteCoreKeys.defaultActivityKey,
              createProgramSiteCoreKeys.deleteException,
            )}
            secondaryButtonTitle={commonLabels.no}
          />
        )}
      </CreateActivityWrapper>
    </MuiModal>
  );
};
